/**
 * Impostazioni, sezione «I tuoi dati»: il fascicolo per il commercialista,
 * l'export completo e la cancellazione dell'account.
 */

import { useState } from "react";
import { Check, Download, Printer, ShieldCheck, Trash2 } from "lucide-react";
import { todayInRome } from "@otto/shared";
import { api, setToken } from "../../lib/api.ts";
import { messaggioDi } from "../../lib/errori.ts";
import { useStore } from "../../lib/store.tsx";
import { Card, Pill, cx } from "../../ui/primitives.tsx";
import { Modal } from "../../ui/Modal.tsx";
import { AperturaFascicolo, Fascicolo, anniFascicolo } from "../Fascicolo.tsx";
import { Campo, CampoPassword } from "../Accesso.tsx";
import { Scheda } from "./comuni.tsx";

export function SezioneDati() {
  const store = useStore();
  const [annoFascicolo, setAnnoFascicolo] = useState<number | null>(null);
  const [esportando, setEsportando] = useState(false);

  /** I propri dati, scaricati come file: è un diritto, non una funzione. */
  const esporta = async () => {
    setEsportando(true);
    try {
      const dati = await api.esportaDati();
      const blob = new Blob([JSON.stringify(dati, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `otto-dati-${todayInRome()}.json`;
      link.click();
      URL.revokeObjectURL(url);
      store.notify("Scaricato. Dentro c'è tutto quello che tengo su di te.");
    } catch (errore) {
      store.notify(messaggioDi(errore));
    } finally {
      setEsportando(false);
    }
  };

  return (
    <>
      <AperturaFascicolo onApri={setAnnoFascicolo} />
      {/* Un'anteprima di stampa: il foglio bianco su fondo grigio, come
          uscirà dalla stampante, e sotto l'anno e il bottone. */}
      <Modal
        open={annoFascicolo != null}
        onClose={() => setAnnoFascicolo(null)}
        title={`Fascicolo ${annoFascicolo ?? ""}`}
        subtitle="Per il commercialista: quello che vedi è quello che stampi."
        media={
          <span className="grid size-10 shrink-0 place-items-center rounded-field bg-surface-muted text-ink-soft">
            <Printer size={18} />
          </span>
        }
        bleed
        footer={
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1 rounded-pill bg-surface p-1 ring-1 ring-line">
              {anniFascicolo().map((anno) => (
                <button
                  key={anno}
                  type="button"
                  onClick={() => setAnnoFascicolo(anno)}
                  aria-pressed={anno === annoFascicolo}
                  className={cx(
                    "rounded-pill px-3.5 py-1.5 text-[13px] font-bold transition-colors duration-quick",
                    anno === annoFascicolo
                      ? "bg-ink text-ink-inverse"
                      : "text-ink-soft hover:text-ink",
                  )}
                >
                  {anno}
                </button>
              ))}
            </div>
            <p className="hidden flex-1 text-[12.5px] leading-snug text-ink-soft md:block">
              Nella finestra di stampa scegli «Salva come PDF». I mesi chiusi
              non cambiano più: esce uguale anche fra tre mesi.
            </p>
            <Pill
              variant="primary"
              size="md"
              icon={<Printer size={15} />}
              onClick={() => window.print()}
              className="ml-auto"
            >
              Stampa o salva in PDF
            </Pill>
          </div>
        }
      >
        <div className="scroll-soft h-full overflow-y-auto bg-surface-muted px-4 py-8 sm:px-10">
          <div className="mx-auto max-w-[900px] rounded-card-lg bg-surface px-6 py-8 shadow-lift ring-1 ring-line sm:px-12 sm:py-12 print:max-w-none print:rounded-none print:p-0 print:shadow-none print:ring-0">
            {annoFascicolo != null ? (
              <Fascicolo anno={annoFascicolo} barra={false} />
            ) : null}
          </div>
        </div>
      </Modal>

      <Scheda
        Icona={Download}
        titolo="Scarica i tuoi dati"
        testo="Immobili, prenotazioni, messaggi degli ospiti, le modifiche fatte e gli accessi. Tutto in un file, quando vuoi."
        azione={
          <Pill
            variant="secondary"
            size="sm"
            icon={<Download size={14} />}
            onClick={esporta}
            loading={esportando}
          >
            Scarica i miei dati
          </Pill>
        }
      />

      <CosaTengo />
      <EliminaAccount onScarica={esporta} scaricando={esportando} />
    </>
  );
}

/** Cosa tiene Otto e dove: detto prima che qualcuno debba chiederlo. */
function CosaTengo() {
  const voci = [
    "Le case, il calendario e le prenotazioni, letti da Guesty.",
    "I messaggi con gli ospiti, per prepararti le risposte.",
    "Le conversazioni con Otto, le proposte e le modifiche approvate.",
    "I conti del mese e le spese che aggiungi tu.",
    "Il tuo nome, la tua email e gli ultimi accessi.",
  ];
  return (
    <Scheda
      Icona={ShieldCheck}
      titolo="Cosa tengo su di te"
      testo="Solo quello che serve a lavorare. Niente viene venduto o dato ad altri; per scrivere le risposte, i testi passano al modello di Anthropic, che non li usa per addestrarsi."
    >
      <ul className="flex flex-col gap-1.5">
        {voci.map((v) => (
          <li
            key={v}
            className="flex items-start gap-2 text-[13px] leading-snug text-ink-soft"
          >
            <Check size={14} className="mt-0.5 shrink-0 text-ok" />
            {v}
          </li>
        ))}
      </ul>
    </Scheda>
  );
}

const FRASE_ELIMINA = "elimina il mio account";

/**
 * Cancellare l'account: il diritto all'oblio, esercitato da soli.
 *
 * Due chiavi, perché non si torna indietro: la password e la frase scritta per
 * intero. Prima del bottone rosso c'è quello per scaricare i dati: dopo non si
 * può più.
 */
function EliminaAccount({
  onScarica,
  scaricando,
}: {
  onScarica: () => void;
  scaricando: boolean;
}) {
  const [aperta, setAperta] = useState(false);
  const [password, setPassword] = useState("");
  const [frase, setFrase] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const pronta =
    password.length > 0 && frase.trim().toLowerCase() === FRASE_ELIMINA;

  const chiudi = () => {
    setAperta(false);
    setPassword("");
    setFrase("");
    setErrore(null);
  };

  const elimina = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!pronta) return;
    setErrore(null);
    setInCorso(true);
    try {
      await api.eliminaAccount(password, frase);
      // Non c'è più niente da mostrare: si torna all'accesso, pulito.
      setToken(null);
      window.location.hash = "";
      window.location.reload();
    } catch (e) {
      setErrore(messaggioDi(e));
      setInCorso(false);
    }
  };

  return (
    <>
      <Card className="border-danger/30 p-5">
        <div className="flex flex-wrap items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-field bg-danger-soft text-danger">
            <Trash2 size={16} />
          </span>
          <div className="min-w-[220px] flex-1">
            <p className="text-[15px] font-bold text-ink">Elimina l'account</p>
            <p className="mt-0.5 text-[13px] leading-relaxed text-ink-soft">
              Cancella per sempre tutto quello che Otto tiene su di te. Su
              Guesty non cambia niente: le case restano dove sono.
            </p>
          </div>
          <Pill variant="danger" size="sm" onClick={() => setAperta(true)}>
            Elimina l'account
          </Pill>
        </div>
      </Card>

      <Modal
        open={aperta}
        onClose={chiudi}
        stretta
        title="Elimina l'account"
        subtitle="Non si torna indietro."
        media={
          <span className="grid size-10 shrink-0 place-items-center rounded-field bg-danger-soft text-danger">
            <Trash2 size={18} />
          </span>
        }
      >
        <form onSubmit={elimina} className="flex flex-col gap-4">
          <div className="rounded-card bg-surface-muted p-4 text-[13px] leading-relaxed text-ink-soft">
            <p className="font-semibold text-ink">Cosa sparisce, subito:</p>
            <p className="mt-1">
              case, prenotazioni e calendario letti da Guesty, i messaggi con
              gli ospiti, le conversazioni con Otto, i conti, le schede delle
              risposte, il registro delle modifiche e gli accessi.
            </p>
            <p className="mt-2">
              Su Guesty non cambia niente. Per togliere del tutto l'accesso a
              Otto, elimina anche l'applicazione su Guesty (Integrations → OAuth
              applications).
            </p>
          </div>

          <button
            type="button"
            onClick={onScarica}
            disabled={scaricando}
            className="flex items-center gap-2 self-start text-[13px] font-semibold text-ink underline-offset-2 hover:underline disabled:opacity-60"
          >
            <Download size={14} />
            {scaricando ? "Preparo il file…" : "Prima scarica i tuoi dati"}
          </button>

          <CampoPassword
            id="elimina-password"
            label="La tua password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
          />
          <Campo
            id="elimina-frase"
            label={`Scrivi «${FRASE_ELIMINA}»`}
            value={frase}
            onChange={setFrase}
            autoComplete="off"
            spellCheck={false}
          />

          {errore ? (
            <p
              role="alert"
              className="rounded-card bg-danger-soft px-3.5 py-3 text-[13.5px] font-semibold text-danger"
            >
              {errore}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Pill
              variant="danger"
              size="md"
              type="submit"
              disabled={!pronta}
              loading={inCorso}
            >
              Elimina per sempre
            </Pill>
            <Pill variant="ghost" size="md" onClick={chiudi}>
              Annulla
            </Pill>
          </div>
        </form>
      </Modal>
    </>
  );
}
